import React, { useState } from 'react';
import { X, Loader2, Link2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { CASE_CATEGORIES, CASE_PRIORITIES, inputClass } from './caseConstants';

// Raise a case from wherever the conversation is happening — a customer,
// booking or order — with the contact details filled in and the record it
// belongs to already attached.
export default function CreateCaseModal({
  customerName = '',
  customerEmail = '',
  customerPhone = '',
  bookingId,
  bookingRef,
  orderId,
  orderRef,
  onClose,
  onCreated,
}) {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('general_support');
  const [priority, setPriority] = useState('medium');
  const [name, setName] = useState(customerName);
  const [email, setEmail] = useState(customerEmail);
  const [phone, setPhone] = useState(customerPhone);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!subject.trim()) {
      setError('Give the case a subject.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await base44.functions.invoke('caseCreate', {
        subject: subject.trim(),
        category,
        priority,
        notes,
        customerName: name,
        customerEmail: email,
        customerPhone: phone,
        bookingId,
        orderId,
      });
      if (res.data?.error) throw new Error(res.data.error);
      if (onCreated) onCreated(res.data.case);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Could not create the case.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#111115] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <p className="text-white text-xs font-bold tracking-widest">NEW SUPPORT CASE</p>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {(bookingRef || orderRef) && (
            <div className="flex items-center gap-2 border border-primary/30 bg-primary/[0.06] px-3 py-2">
              <Link2 className="w-3.5 h-3.5 text-primary shrink-0" />
              <p className="text-white/60 text-[11px] truncate">
                Linked to {[bookingRef && `booking ${bookingRef}`, orderRef && `order ${orderRef}`].filter(Boolean).join(' · ')}
              </p>
            </div>
          )}

          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">SUBJECT</p>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What is this about?"
              className={inputClass}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">CATEGORY</p>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
                {CASE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">PRIORITY</p>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputClass}>
                {CASE_PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">CUSTOMER NAME</p>
              <input value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
            </div>
            <div>
              <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">PHONE</p>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
            </div>
          </div>

          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">EMAIL</p>
            <input value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
          </div>

          <div>
            <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">INTERNAL NOTES</p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Anything the team should know before picking this up."
              className={`${inputClass} resize-none leading-relaxed`}
            />
          </div>

          {error && <p className="text-red-400 text-xs">{error}</p>}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              onClick={onClose}
              className="text-white/40 hover:text-white text-[10px] font-bold tracking-widest px-4 py-2.5 border border-white/10 hover:border-white/25 transition-colors"
            >
              CANCEL
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-[10px] tracking-widest px-5 py-2.5 transition-colors disabled:opacity-40"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? 'CREATING…' : 'CREATE CASE'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}